/**
 * CloneCTAForRepModal — R2B (2026-05-17), QR-on-clone fix Sprint A
 * Batch 1 A10.2 (2026-06-03).
 *
 * Opens from a CTACard's "+ Clone for another rep" button. Duplicates
 * the source CTA's configuration but assigns a DIFFERENT sales_position_id
 * so the new CTA gets its own rep-attributed QR code.
 *
 * Audit verdict Decision 5 chose pattern (C): multi-select on create
 * AND clone-button. This is the clone half.
 *
 * Implementation note: we don't roll a server-side "clone" RPC; we
 * call createEventCTA with the source CTA's fields + the new
 * sales_position_id, then explicitly call generateCTAQRCode for the
 * new CTA. The original R2B implementation's comment claimed
 * "existing create logic (QR generation, validation) run unchanged"
 * but createEventCTA does NOT generate QR — that runs only when the
 * user clicks the Generate QR button on a CTACard. Heather's report
 * ("I don't see the QR code when cloned") was caused by that missing
 * call. Audit-AI GREEN: don't fail/rollback the clone if QR
 * generation fails — show a partial-success toast and let the user
 * retry via the Generate QR button on the new CTA card.
 */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../services/supabase";
import {
  createEventCTA,
  generateCTAQRCode,
} from "../../services/eventCTA.service";
import { useAuth } from "../../contexts/AuthContext";
import Button from "../common/Button";
import Modal from "../ui/Modal";
import { useToast } from "../ui/Toast";
import type { EventCallToAction, Event } from "../../types/event.types";
import type { UUID } from "../../types/common.types";

interface SalesPositionRow {
  id: UUID;
  title: string;
  assigned_name: string | null;
  is_active: boolean;
}

interface CloneCTAForRepModalProps {
  sourceCTA: EventCallToAction;
  eventId: UUID;
  /**
   * The parent event — required for generateCTAQRCode after clone
   * succeeds. Passed in by CTACard which already has it in scope.
   */
  event: Event;
  onClose: () => void;
  onCloned: () => void;
}

const CloneCTAForRepModal: React.FC<CloneCTAForRepModalProps> = ({
  sourceCTA,
  eventId,
  event,
  onClose,
  onCloned,
}) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [selectedRepId, setSelectedRepId] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const { data: salesPositions } = useQuery({
    queryKey: ["sales-positions-active-for-clone"],
    queryFn: async () => {
      const { data } = await supabase
        .from("sales_positions")
        .select("id, title, assigned_name, is_active")
        .order("assigned_name", { ascending: true, nullsFirst: false });
      return (data ?? []) as SalesPositionRow[];
    },
  });

  const handleClone = async () => {
    if (!selectedRepId || !user?.id) {
      return;
    }
    setSubmitting(true);
    try {
      const repLabel =
        salesPositions?.find((p) => p.id === selectedRepId)?.assigned_name ??
        salesPositions?.find((p) => p.id === selectedRepId)?.title ??
        "rep";

      const createdCTA = await createEventCTA(
        {
          event_id: eventId,
          name: `${sourceCTA.name} — ${repLabel}`,
          cta_type: sourceCTA.cta_type,
          base_url: sourceCTA.base_url ?? undefined,
          parameters: sourceCTA.parameters ?? {},
          reward_type: sourceCTA.reward_type,
          offer_id: sourceCTA.offer_id ?? undefined,
          voucher_amount: sourceCTA.voucher_amount ?? undefined,
          voucher_start_date: sourceCTA.voucher_start_date ?? undefined,
          voucher_end_date: sourceCTA.voucher_end_date ?? undefined,
          voucher_expiry_days: sourceCTA.voucher_expiry_days,
          voucher_expires_at_event_end: sourceCTA.voucher_expires_at_event_end,
          include_state_param: sourceCTA.include_state_param,
          // Carry it, or cloning a CTA silently drops the option back to off.
          use_state_defaults: sourceCTA.use_state_defaults === true,
          single_voucher: sourceCTA.single_voucher === true,
          use_member_signup: sourceCTA.use_member_signup,
          display_away_plans: sourceCTA.display_away_plans,
          sales_position_id: selectedRepId,
        },
        user.id,
      );
      // A10.2: chain QR generation after clone. Don't fail the clone
      // if QR generation errors — surface a partial-success toast and
      // leave the user to retry via the Generate QR button on the new
      // CTA card. The clone is the value-bearing operation; the QR is
      // a separate asset that the user could rebuild manually.
      try {
        await generateCTAQRCode(createdCTA.id, event, user.id);
        showToast("success", `Cloned CTA for ${repLabel}`);
      } catch (qrErr) {
        console.warn(
          "CloneCTAForRepModal: clone succeeded but QR generation failed",
          qrErr,
        );
        showToast(
          "warning",
          `Cloned for ${repLabel}, but QR generation failed — click Generate QR on the new CTA`,
        );
      }
      onCloned();
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to clone CTA",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Clone CTA for another rep">
      <div className="space-y-4">
        <p className="text-sm text-gray-700">
          Creates a copy of <strong>{sourceCTA.name}</strong> attributed to a
          different rep. The new CTA gets its own QR code showing the new rep's
          name + the event.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New credited rep
          </label>
          <select
            value={selectedRepId}
            onChange={(e) => setSelectedRepId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
            autoFocus
          >
            <option value="">— Select a rep —</option>
            {(salesPositions ?? []).map((pos) => (
              <option key={pos.id} value={pos.id}>
                {pos.assigned_name || pos.title}
                {!pos.is_active ? " — inactive" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleClone}
            disabled={!selectedRepId || submitting}
          >
            {submitting ? "Cloning..." : "Clone"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CloneCTAForRepModal;
